import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { SidebarService } from '../../services/components/sidebar/sidebar.service';
import { Router, NavigationEnd } from '@angular/router';
import { environment } from 'src/environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { AuthService } from '../../services/auth/auth.service';
import { UserModeService } from 'src/app/services/customs/user-mode/user-mode.service';
import { Subject } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, OnDestroy {
  @Input() isCollapsed = false;
  @Output() collapsedChange = new EventEmitter<boolean>();

  submenuVisibility: boolean[] = [];
  currentUrl: string = '';
  menuOptions: any[] = [];
  // Drives skeleton/error/loaded rendering.
  menuStatus: 'loading' | 'loaded' | 'error' = 'loading';
  // Placeholder rows for the loading skeleton.
  skeletonRows = new Array(7);
  public showSwitchOption = false;
  mainAppBaseURL: string = '';
  activeMode: 'customer' | 'provider' = 'customer';
  cookieName: string = '';

  isHovered = false;
  private savedScrollTop = 0;

  private pendingScrollTimeout: any = null;
  private removePendingTransitionListener: (() => void) | null = null;

  // Flyout and Label positioning
  flyoutVisibility: boolean[] = [];
  flyoutTops: { [key: number]: number } = {};
  labelTops: { [key: number]: number } = {};
  private flyoutCloseTimers: { [key: number]: any } = {};

  // Tears down subscriptions on destroy.
  private destroy$ = new Subject<void>();

  constructor(
    private sidebarService: SidebarService,
    private router: Router,
    private cookieService: CookieService,
    private authService: AuthService,
    private userModeService: UserModeService,
  ) {
    this.mainAppBaseURL = environment.appUrl;
    this.currentUrl = this.router.url;
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentUrl = event.urlAfterRedirects;
        // Re-center the sidebar on the active item whenever the route changes
        this.scrollActiveItemIntoView();
      }
    });
  }

  ngOnInit(): void {
    this.userModeService.hasProvider$
      .pipe(takeUntil(this.destroy$))
      .subscribe((hasProvider) => {
        this.showSwitchOption = hasProvider;
      });

    // Paint from cache on return.
    const cached = this.sidebarService.getCachedMenu();
    if (cached && cached.length) {
      this.menuOptions = cached;
      this.menuStatus = 'loaded';
    }

    this.userModeService.mode$
      .pipe(
        filter((mode): mode is 'customer' | 'provider' => mode !== null),
        switchMap((mode) => {
          this.activeMode = mode;
          // Skeleton only on cold load; keep stale data while revalidating.
          if (!this.menuOptions.length) {
            this.menuStatus = 'loading';
          }
          return this.sidebarService.getSidebar(mode);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (menu) => {
          this.menuOptions = menu;
          this.menuStatus = 'loaded';
          this.sidebarService.setActiveTab();
          // Wait for the expand transition before measuring the scroll target.
          if (this.expandActiveParentSubmenu()) {
            this.scrollAfterSubmenuExpand();
          } else {
            this.scrollActiveItemIntoView();
          }
        },
        error: () => {
          this.menuStatus = 'error';
        },
      });
  }

  // Re-fetches the menu for the current mode after a load failure.
  retryLoadMenu(): void {
    this.menuStatus = 'loading';
    this.sidebarService
      .getSidebar(this.activeMode)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (menu) => {
          this.menuOptions = menu;
          this.menuStatus = 'loaded';
          this.sidebarService.setActiveTab();
          if (this.expandActiveParentSubmenu()) {
            this.scrollAfterSubmenuExpand();
          } else {
            this.scrollActiveItemIntoView();
          }
        },
        error: () => {
          this.menuStatus = 'error';
        },
      });
  }

  trackByMenuId = (_: number, m: any) => m?.id;

  // Opens the active route's submenu; returns true if newly opened.
  private expandActiveParentSubmenu(): boolean {
    let newlyExpanded = false;
    this.menuOptions.forEach((m, i) => {
      if (this.isChildActive(m)) {
        if (!this.submenuVisibility[i]) {
          newlyExpanded = true;
        }
        this.submenuVisibility[i] = true;
      }
    });
    return newlyExpanded;
  }

  // Scrolls once the submenu finishes expanding, with a timeout fallback.
  private scrollAfterSubmenuExpand(): void {
    // Wait a frame so .submenu-visible is applied before querying.
    requestAnimationFrame(() => {
      const container = this.getScrollableElement();
      const active = container?.querySelector<HTMLElement>(
        '.sidebar-item.active',
      );
      const submenu = active?.closest<HTMLElement>('.submenu');

      if (!submenu) {
        this.scrollActiveItemIntoView();
        return;
      }

      let done = false;
      const runOnce = () => {
        if (done) return;
        done = true;
        this.clearPendingScroll();
        this.scrollActiveItemIntoView();
      };

      const onTransitionEnd = (event: TransitionEvent) => {
        // max-height is the layout-affecting property; ignore opacity.
        if (event.propertyName !== 'max-height') return;
        runOnce();
      };

      submenu.addEventListener('transitionend', onTransitionEnd);
      this.removePendingTransitionListener = () =>
        submenu.removeEventListener('transitionend', onTransitionEnd);

      // Fallback slightly beyond the 500ms CSS transition.
      this.pendingScrollTimeout = setTimeout(runOnce, 600);
    });
  }

  private clearPendingScroll(): void {
    if (this.removePendingTransitionListener) {
      this.removePendingTransitionListener();
      this.removePendingTransitionListener = null;
    }
    if (this.pendingScrollTimeout) {
      clearTimeout(this.pendingScrollTimeout);
      this.pendingScrollTimeout = null;
    }
  }

  /**
   * Scrolls the sidebar's scrollable area so the active item is comfortably
   * visible (centered when possible). Runs after the view has rendered so the
   * user immediately sees which page they're on.
   */
  private scrollActiveItemIntoView(): void {
    // Defer to the next frame so the DOM reflects the current active state.
    requestAnimationFrame(() => {
      const container = this.getScrollableElement();
      if (!container) return;

      const active = container.querySelector<HTMLElement>(
        '.sidebar-item.active',
      );
      if (!active) return;

      // Use bounding rects so nested items (inside submenus) resolve correctly
      // regardless of offsetParent. Center the active item when there's room.
      const containerRect = container.getBoundingClientRect();
      const activeRect = active.getBoundingClientRect();
      const activeOffsetWithin =
        activeRect.top - containerRect.top + container.scrollTop;

      const target =
        activeOffsetWithin -
        container.clientHeight / 2 +
        active.clientHeight / 2;
      const maxScroll = container.scrollHeight - container.clientHeight;
      const nextTop = Math.max(0, Math.min(target, maxScroll));

      container.scrollTo({ top: nextTop, behavior: 'smooth' });
      // Keep the collapse/expand restore logic in sync.
      this.savedScrollTop = nextTop;
    });
  }

  switchMode(): void {
    // Only users allowed to be a provider may switch modes.
    if (!this.showSwitchOption) {
      return;
    }

    const newMode: 'customer' | 'provider' =
      this.activeMode === 'customer' ? 'provider' : 'customer';

    this.userModeService.setMode(newMode);

    this.router.navigate(['/dashboard']);
  }

  toggleSubmenu(index: number): void {
    this.submenuVisibility[index] = !this.submenuVisibility[index];
  }

  private isMobile(): boolean {
    return window.innerWidth < 768;
  }

  onMouseEnter() {
    if (this.isMobile()) return;
    this.isHovered = true;
    this.isCollapsed = false;
    this.collapsedChange.emit(this.isCollapsed);

    // Restore scroll position after expand
    requestAnimationFrame(() => {
      const el = this.getScrollableElement();
      if (el) el.scrollTop = this.savedScrollTop;
    });
  }

  onMouseLeave() {
    if (this.isMobile()) return;

    // Save scroll position before collapse
    const el = this.getScrollableElement();
    if (el) this.savedScrollTop = el.scrollTop;

    this.isHovered = false;
    this.isCollapsed = true;
    this.collapsedChange.emit(this.isCollapsed);
    this.closeAllFlyouts();
  }

  private getScrollableElement(): HTMLElement | null {
    const sidebar = document.querySelector('.sidebar-dynamic');
    return sidebar as HTMLElement | null;
  }

  isChildActive(menuItem: any): boolean {
    if (!menuItem.children || menuItem.children.length === 0) {
      return false;
    }
    return menuItem.children.some((child: { route: string }) =>
      this.isRouteActive(child.route),
    );
  }

  isSubmenuVisible(index: number): boolean {
    return this.submenuVisibility[index];
  }

  get _logoutURL() {
    return `${this.mainAppBaseURL}/logout`;
  }

  get _helpURL() {
    return 'https://help.truckit.net/support/home';
  }

  get _howItWorksURL() {
    return `${this.mainAppBaseURL}/how-it-works`;
  }

  getCurrentActiveTab() {
    return this.sidebarService.getActiveTab();
  }

  isRouteActive(route: string): boolean {
    const normalize = (url: string): string =>
      url.replace(/^\/+/, '').replace(/\/+$/, '');
    const normalizedRoute = normalize(route);
    const normalizedCurrentUrl = normalize(this.currentUrl);

    if (normalizedRoute == '') return false;
    // Boundary check so 'esg' doesn't match a sibling like 'esg-report'.
    return (
      normalizedCurrentUrl === normalizedRoute ||
      normalizedCurrentUrl.startsWith(normalizedRoute + '/')
    );
  }

  public navigate(route: string) {
    this.closeAllFlyouts();

    if (route.startsWith('http')) {
      window.location.href = route;
    } else {
      this.router.navigate([route]).finally(() => {
        this.sidebarService.setActiveTab();
      });
    }
  }

  toggleSidebar() {
    this.isCollapsed = !this.isCollapsed;
    if (this.isCollapsed) {
      this.isHovered = false;
    }
    this.collapsedChange.emit(this.isCollapsed);
    this.closeAllFlyouts();
  }

  // --- Flyout hover logic ---

  onMenuItemEnter(index: number, event: MouseEvent) {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.flyoutTops[index] = rect.top;
    this.labelTops[index] = rect.top + rect.height / 2;

    if (this.flyoutCloseTimers[index]) {
      clearTimeout(this.flyoutCloseTimers[index]);
      delete this.flyoutCloseTimers[index];
    }
    this.flyoutVisibility.forEach((_, i) => {
      if (i !== index) {
        this.flyoutVisibility[i] = false;
      }
    });
    this.flyoutVisibility[index] = true;
  }

  onMenuItemLeave(index: number) {
    this.flyoutCloseTimers[index] = setTimeout(() => {
      this.flyoutVisibility[index] = false;
      delete this.flyoutCloseTimers[index];
    }, 300);
  }

  onFlyoutEnter(index: number) {
    if (this.flyoutCloseTimers[index]) {
      clearTimeout(this.flyoutCloseTimers[index]);
      delete this.flyoutCloseTimers[index];
    }
  }

  onFlyoutLeave(index: number) {
    this.flyoutVisibility[index] = false;
  }

  private closeAllFlyouts() {
    this.flyoutVisibility = [];
    Object.values(this.flyoutCloseTimers).forEach((timer) =>
      clearTimeout(timer),
    );
    this.flyoutCloseTimers = {};
  }

  ngOnDestroy() {
    Object.values(this.flyoutCloseTimers).forEach((timer) =>
      clearTimeout(timer),
    );
    this.clearPendingScroll();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
